import type { ReactNode } from 'react';

import Text from '@cartwhl/ui/base/Text';

import type { CombinedPanelTab } from '../useWorkspaceLayout';
import FloatingPanel from '../FloatingPanel';

const COMBINED_TAB_LABEL_PADDING_PX = 22;
const COMBINED_TAB_LABEL_GAP_PX = 6;
const COMBINED_TAB_LABEL_CHAR_WIDTH_PX = 7;
const COMBINED_TAB_LABEL_MIN_CHARS = 8;
const COMBINED_TAB_BASE_CLASS =
  'inline-flex h-8 min-w-0 flex-1 items-center justify-center overflow-hidden rounded-t-xl rounded-b-none border border-b-0 px-2 transition-colors';
const COMBINED_TAB_ACTIVE_CLASS =
  'bg-white/95 text-gray-90 border-gray-30/80 dark:bg-gray-90 dark:text-white dark:border-gray-60/80';
const COMBINED_TAB_INACTIVE_CLASS =
  'bg-white/40 text-gray-45 border-gray-30/60 hover:bg-white/55 dark:bg-gray-75/45 dark:text-gray-30 dark:border-gray-60/60 dark:hover:bg-gray-75/65';

function truncateWithEllipsisSuffix(value: string, maxChars: number): string {
  if (value.length <= maxChars) return value;
  if (maxChars <= 3) return '...';
  return `${value.slice(0, maxChars - 3)}...`;
}

interface WorkspacePanelsProps {
  hasEverLoadedScene: boolean;
  hasRenderableSkeleton: boolean;
  hideSidePanels: boolean;
  usesCombinedLayout: boolean;
  detailPanelTitle: string;
  detailPanelTitleClassName?: string;
  detailPanelContent: ReactNode;
  detailPanelContentClassName: string;
  isBoneAssignmentsActive: boolean;
  outlinerFooterSettingsControl: ReactNode;
  outlinerContent: ReactNode;
  outlinerWidth: number;
  outlinerHeight: number;
  isOutlinerOpen: boolean;
  onToggleOutlinerOpen: () => void;
  onOutlinerWidthChange: (nextWidth: number) => void;
  onOutlinerHeightChange: (nextHeight: number) => void;
  outlinerMinWidth: number;
  outlinerMaxWidth: number;
  inspectorWidth: number;
  inspectorHeight: number;
  isInspectorOpen: boolean;
  onToggleInspectorOpen: () => void;
  onInspectorWidthChange: (nextWidth: number) => void;
  onInspectorHeightChange: (nextHeight: number) => void;
  inspectorMaxWidth: number;
  activeCombinedTab: CombinedPanelTab;
  onSelectCombinedTab: (nextTab: CombinedPanelTab) => void;
  combinedPanelResolvedWidth: number;
  combinedPanelHeight: number;
  isCombinedPanelOpen: boolean;
  onToggleCombinedPanelOpen: () => void;
  onCombinedPanelWidthChange: (nextWidth: number) => void;
  onCombinedPanelHeightChange: (nextHeight: number) => void;
  combinedPanelMinWidth: number;
  combinedPanelMaxWidth: number;
}

export default function WorkspacePanels({
  hasEverLoadedScene,
  hasRenderableSkeleton,
  hideSidePanels,
  usesCombinedLayout,
  detailPanelTitle,
  detailPanelTitleClassName,
  detailPanelContent,
  detailPanelContentClassName,
  isBoneAssignmentsActive,
  outlinerFooterSettingsControl,
  outlinerContent,
  outlinerWidth,
  outlinerHeight,
  isOutlinerOpen,
  onToggleOutlinerOpen,
  onOutlinerWidthChange,
  onOutlinerHeightChange,
  outlinerMinWidth,
  outlinerMaxWidth,
  inspectorWidth,
  inspectorHeight,
  isInspectorOpen,
  onToggleInspectorOpen,
  onInspectorWidthChange,
  onInspectorHeightChange,
  inspectorMaxWidth,
  activeCombinedTab,
  onSelectCombinedTab,
  combinedPanelResolvedWidth,
  combinedPanelHeight,
  isCombinedPanelOpen,
  onToggleCombinedPanelOpen,
  onCombinedPanelWidthChange,
  onCombinedPanelHeightChange,
  combinedPanelMinWidth,
  combinedPanelMaxWidth,
}: WorkspacePanelsProps) {
  const combinedPanelTitle =
    activeCombinedTab === 'outliner' ? 'Outliner' : detailPanelTitle;
  const combinedPanelTitleClassName =
    activeCombinedTab === 'detail' ? detailPanelTitleClassName : undefined;
  const estimatedPerTabTextWidthPx = Math.max(
    0,
    combinedPanelResolvedWidth / 2 -
      COMBINED_TAB_LABEL_PADDING_PX -
      COMBINED_TAB_LABEL_GAP_PX,
  );
  const combinedInspectorTabMaxChars = Math.max(
    COMBINED_TAB_LABEL_MIN_CHARS,
    Math.floor(estimatedPerTabTextWidthPx / COMBINED_TAB_LABEL_CHAR_WIDTH_PX),
  );
  const detailCombinedTabTitle =
    activeCombinedTab === 'detail'
      ? truncateWithEllipsisSuffix(detailPanelTitle, combinedInspectorTabMaxChars)
      : isBoneAssignmentsActive
        ? 'Assignments'
        : 'Inspector';
  const detailCombinedTabTitleClassName =
    activeCombinedTab === 'detail' ? detailPanelTitleClassName : undefined;

  return (
    <>
      {hasEverLoadedScene && !usesCombinedLayout && (
        <FloatingPanel
          title="Outliner"
          side="left"
          width={outlinerWidth}
          height={outlinerHeight}
          isOpen={isOutlinerOpen}
          isTemporarilyHidden={hideSidePanels}
          onToggleOpen={onToggleOutlinerOpen}
          onWidthChange={onOutlinerWidthChange}
          onHeightChange={onOutlinerHeightChange}
          minWidth={outlinerMinWidth}
          maxWidth={outlinerMaxWidth}
          minHeight={280}
          footerContent={outlinerFooterSettingsControl}
        >
          <div className="h-full p-2">{hasRenderableSkeleton && outlinerContent}</div>
        </FloatingPanel>
      )}

      {hasEverLoadedScene && !usesCombinedLayout && (
        <FloatingPanel
          title={detailPanelTitle}
          titleClassName={detailPanelTitleClassName}
          side="right"
          width={inspectorWidth}
          height={inspectorHeight}
          isOpen={isInspectorOpen}
          isTemporarilyHidden={hideSidePanels}
          onToggleOpen={onToggleInspectorOpen}
          onWidthChange={onInspectorWidthChange}
          onHeightChange={onInspectorHeightChange}
          minWidth={280}
          maxWidth={inspectorMaxWidth}
          minHeight={280}
        >
          <div className={detailPanelContentClassName}>{detailPanelContent}</div>
        </FloatingPanel>
      )}

      {hasEverLoadedScene && usesCombinedLayout && (
        <FloatingPanel
          title={combinedPanelTitle}
          titleClassName={combinedPanelTitleClassName}
          side="left"
          width={combinedPanelResolvedWidth}
          height={combinedPanelHeight}
          isOpen={isCombinedPanelOpen}
          isTemporarilyHidden={hideSidePanels}
          onToggleOpen={onToggleCombinedPanelOpen}
          onWidthChange={onCombinedPanelWidthChange}
          onHeightChange={onCombinedPanelHeightChange}
          minWidth={combinedPanelMinWidth}
          maxWidth={combinedPanelMaxWidth}
          minHeight={280}
          footerContent={
            activeCombinedTab === 'outliner' ? outlinerFooterSettingsControl : null
          }
          headerContent={
            isCombinedPanelOpen ? (
              <div className="flex h-full min-w-0 items-end gap-0.5 pt-1">
                <button
                  type="button"
                  aria-label="Show outliner panel"
                  aria-pressed={activeCombinedTab === 'outliner'}
                  onClick={() => onSelectCombinedTab('outliner')}
                  className={`${COMBINED_TAB_BASE_CLASS} ${
                    activeCombinedTab === 'outliner'
                      ? COMBINED_TAB_ACTIVE_CLASS
                      : COMBINED_TAB_INACTIVE_CLASS
                  }`}
                >
                  <Text
                    variant="studio/body/medium/sm"
                    className="block min-w-0 truncate px-0.5"
                  >
                    Outliner
                  </Text>
                </button>
                <button
                  type="button"
                  aria-label={`Show ${
                    isBoneAssignmentsActive ? 'bone assignments' : 'inspector'
                  } panel`}
                  aria-pressed={activeCombinedTab === 'detail'}
                  onClick={() => onSelectCombinedTab('detail')}
                  className={`${COMBINED_TAB_BASE_CLASS} ${
                    activeCombinedTab === 'detail'
                      ? COMBINED_TAB_ACTIVE_CLASS
                      : COMBINED_TAB_INACTIVE_CLASS
                  }`}
                >
                  <Text
                    variant="studio/body/medium/sm"
                    className={`block min-w-0 truncate px-0.5 ${detailCombinedTabTitleClassName ?? ''}`}
                  >
                    {detailCombinedTabTitle}
                  </Text>
                </button>
              </div>
            ) : null
          }
        >
          <div className="h-full p-2">
            {hasRenderableSkeleton &&
              (activeCombinedTab === 'outliner' ? outlinerContent : detailPanelContent)}
          </div>
        </FloatingPanel>
      )}
    </>
  );
}
